/** Email + password account creation — the counterpart to `Login.tsx`. */

import { useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router'
import { useAuth } from '../auth/AuthContext'
import { GoogleSignInButton } from '../auth/GoogleSignInButton'
import { LangSwitch } from '../components/LangSwitch'
import { PivotLogo } from '../components/PivotLogo'
import { ThemeToggle } from '../components/ThemeToggle'
import { useI18n } from '../i18n'
import '../styles/auth-forms.css'
import '../styles/header-actions.css'

export default function Signup() {
  const { t, locale } = useI18n()
  const { user, signupWithPassword } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  if (user) return <Navigate to={user.profile_complete ? '/dashboard' : '/onboarding'} replace />

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await signupWithPassword(email, password, locale)
      navigate('/onboarding')
    } catch {
      setError(t('auth.genericError'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="auth-page">
      <title>{`${t('auth.signupTitle')} — ${t('common.appName')}`}</title>
      <header className="auth-bar">
        <Link to="/" className="auth-brand" aria-label={t('a11y.authHomeLink')}>
          <PivotLogo />
          {t('common.appName')}
        </Link>
        <div className="header-actions">
          <LangSwitch />
          <ThemeToggle />
        </div>
      </header>
      <div className="auth-body">
        <div className="auth-card">
          <h1>{t('auth.signupTitle')}</h1>
          <GoogleSignInButton />
          <div className="auth-divider">{t('auth.orDivider')}</div>
          <form onSubmit={handleSubmit} className="stack auth-form">
            <label>
              {t('auth.emailLabel')}
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoComplete="email"
              />
            </label>
            <label>
              {t('auth.passwordLabel')}
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </label>
            <p className="auth-form-hint">{t('auth.signupPasswordHint')}</p>
            {error && <p className="form-error">{error}</p>}
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {t('auth.signupCta')}
            </button>
          </form>
          <p className="auth-links">
            {t('auth.signupHaveAccount')}{' '}
            <Link to="/login" className="auth-link">
              {t('auth.signupSignIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
